import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Page } from '../../models/page-wrapper.model';
import { ApprovalActionRequest } from './approval-action.model';
import { InitiateApprovalRequest } from './initiate-approval.model';
import { WorkflowApproval } from './workflow-apporval.model';
import { PendingApprovalSummary } from './workflow-apporval.model';
import { environment } from '../../../environments/environment';


/**
 * Service to interact with the /api/workflow/history endpoint.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowApprovalService {

  private apiUrl = `${environment.restApiUrl}workflow/history`;

  constructor(private http: HttpClient) { }

  /**
   * Retrieves a paginated list of workflow history records.
   * @param params HttpParams for pagination and filtering (e.g., page, size, sort).
   * Note: Page size defaults to 200 if not explicitly provided.
   */
  getAllHistory(params: HttpParams): Observable<Page<WorkflowApproval>> {
    // Ensure a default page size of 200 if not provided
    if (!params.has('size')) {
      params = params.set('size', '200');
    }
    return this.http.get<Page<WorkflowApproval>>(this.apiUrl, { params });
  }

  /**
   * Retrieves one paginated row per business object for the approvals list.
   * This avoids downloading and aggregating the full workflow history in the browser.
   */
  getPendingApprovalSummaries(
    requiredRoleId: number,
    resourceTypeId: number,
    page = 0,
    size = 50
  ): Observable<Page<PendingApprovalSummary>> {
    const params = new HttpParams()
      .set('requiredRoleId', requiredRoleId.toString())
      .set('resourceTypeId', resourceTypeId.toString())
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<Page<PendingApprovalSummary>>(`${this.apiUrl}/pending-summary`, { params });
  }

  /**
   * Retrieves a single workflow history record by its ID.
   * @param id The ID of the history record.
   */
  getHistoryById(id: number): Observable<WorkflowApproval> {
    return this.http.get<WorkflowApproval>(`${this.apiUrl}/${id}`);
  }

  /**
   * Finds all current pending approval history records for a given business object.
   * @param resourceTypeId The ID of the resource type.
   * @param businessObjectId The ID of the business object instance.
   */
  getPendingApprovals(resourceTypeId: number, businessObjectId: number): Observable<WorkflowApproval[]> {
    const params = new HttpParams()
      .set('resourceTypeId', resourceTypeId.toString())
      .set('businessObjectId', businessObjectId.toString());
    return this.http.get<WorkflowApproval[]>(`${this.apiUrl}/pending`, { params });
  }

  /**
   * Initiates new pending approval history entries for a business object.
   * @param request The request body containing the necessary IDs.
   */
  initiateApprovals(request: InitiateApprovalRequest): Observable<WorkflowApproval[]> {
    return this.http.post<WorkflowApproval[]>(`${this.apiUrl}/initiate`, request);
  }

  /**
   * Records an approval action (e.g., 'APPROVED', 'REJECTED') for a pending step.
   * @param id The ID of the pending approval history record.
   * @param request The request body containing the action and comments.
   */
  recordAction(id: number, request: ApprovalActionRequest): Observable<WorkflowApproval> {
    return this.http.post<WorkflowApproval>(`${this.apiUrl}/${id}/action`, request);
  }

  /**
   * Deletes a workflow history record by its ID.
   * @param id The ID of the history record to delete.
   */
  deleteHistoryById(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
